import { useEffect, useState } from "react";
import {
  Activity, RefreshCw, AlertTriangle, CheckCircle2, XCircle, Server, Database, Plane, Map as MapIcon, Layers,
  Clock, ShieldAlert, StickyNote,
} from "lucide-react";

import styles from "./Monitoring.module.css";
import { useToast } from "../../../context/ToastContext";
import {
  useMonitoring,
  useIncidents,
  updateIncident,
  apiErrorMessage,
} from "../../../api/sytem&settings/useMonitoring";
import { formatDMYTime } from "../../../utils/dateFormat";
import {
  partError,
  panelState,
  cacheUnhappy,
  hotelbedsUnhappy,
  flightsUnhappy,
  packagesUnhappy,
  w2mUnhappy,
  ageLabel,
  minutesLabel,
  memoryLabel,
  timeLabel,
  recentRuns,
  runDuration,
  verdictView,
} from "./monitoringView";
import {
  INCIDENT_TABS,
  INCIDENT_ACTIONS,
  sortBySeverity,
  severityLabel,
  areaLabel,
  occurrencesLabel,
  resolutionLabel,
  takenByLabel,
  emailLabel,
  availableActions,
  incidentPatch,
  actionDoneMessage,
  emptyIncidentsText,
  pageInfo,
} from "./incidentsView";

/**
 * System → Monitoring (Master Ch 1 §12A, UI-26). Also opened as /monitoring, the address the
 * incident alert emails link to.
 *
 * One page an admin can look at to answer "is anything wrong": an overall verdict with the
 * reasons and when the incident watch last checked, then a panel per moving part, then the
 * incident history (one row per problem, from Open to Resolved).
 *
 * The rule the whole screen is built on: a panel that could not be read says so. The endpoint
 * answers an unreachable part with `{ error }` rather than failing the page, and rendering that
 * as an empty panel would make a dead search cache look exactly like a quiet one. Every panel
 * therefore has three states and the error state is loud. The incident list is loaded on its
 * own, so either half failing leaves the other on screen.
 */

const Row = ({ label, children }) => (
  <div className={styles.row}>
    <span className={styles.rowLabel}>{label}</span>
    <span className={styles.rowValue}>{children}</span>
  </div>
);

const Panel = ({ title, Icon, state, error, children }) => (
  <section className={`${styles.panel} ${styles[`panel${state}`]}`}>
    <header className={styles.panelHead}>
      <span className={styles.panelIcon}><Icon size={16} /></span>
      <h2 className={styles.panelTitle}>{title}</h2>
      <span className={`${styles.pill} ${styles[`pill${state}`]}`}>
        {state === "error" ? "Unreadable" : state === "attention" ? "Attention" : "OK"}
      </span>
    </header>

    {state === "error" ? (
      <p className={styles.panelError}>
        <XCircle size={14} />
        {error || "This part did not report. It may be stopped, or the admin could not reach it."}
      </p>
    ) : children}
  </section>
);

const Fact = ({ label, children }) => (
  <div className={styles.fact}>
    <span className={styles.factLabel}>{label}</span>
    <span className={styles.factValue}>{children}</span>
  </div>
);

const STATUS_CLASS = { Open: "statusOpen", "In progress": "statusProgress", Resolved: "statusResolved" };

/**
 * One incident. Active ones carry "Take (In progress)" (Open only) and "Resolve"; either opens a
 * small form for an optional note before anything is sent.
 */
const IncidentCard = ({ incident, onAct }) => {
  const [mode, setMode] = useState(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const sev = incident.severity === "critical" ? "critical" : "warning";
  const actions = availableActions(incident);
  const resolution = resolutionLabel(incident);
  const takenBy = takenByLabel(incident);
  const noteId = `incident-note-${incident.id}`;

  const open = (action) => { setMode(action); setNote(""); };
  const cancel = () => { setMode(null); setNote(""); };
  const submit = async () => {
    setBusy(true);
    const ok = await onAct(incident, mode, note);
    setBusy(false);
    if (ok) cancel();
  };

  return (
    <article className={`${styles.incident} ${styles[`incident${sev}`]}`} aria-label={incident.title}>
      <div className={styles.incidentHead}>
        <span className={`${styles.sevPill} ${styles[`sev${sev}`]}`}>{severityLabel(incident.severity)}</span>
        <h3 className={styles.incidentTitle}>{incident.title}</h3>
        <span className={`${styles.statusPill} ${styles[STATUS_CLASS[incident.status] || "statusResolved"]}`}>
          {incident.status}
        </span>
      </div>

      {incident.cause && <p className={styles.incidentCause}>{incident.cause}</p>}

      <div className={styles.facts}>
        <Fact label="Area">{areaLabel(incident.source)}</Fact>
        <Fact label="First seen">{formatDMYTime(incident.firstSeenAt)}</Fact>
        <Fact label="Last seen">{formatDMYTime(incident.lastSeenAt)}</Fact>
        <Fact label="Found by">{occurrencesLabel(incident.occurrences)}</Fact>
        {takenBy && <Fact label="Taken by">{takenBy}</Fact>}
        {resolution && <Fact label="Resolved">{resolution}</Fact>}
        <Fact label="Alert email">{emailLabel(incident)}</Fact>
      </div>

      {incident.note && (
        <p className={styles.incidentNote}>
          <StickyNote size={13} />
          <span>{incident.note}</span>
        </p>
      )}

      {actions.length > 0 && !mode && (
        <div className={styles.incidentActions}>
          {actions.includes("take") && (
            <button type="button" className={styles.takeBtn} onClick={() => open("take")}>
              {INCIDENT_ACTIONS.take.label}
            </button>
          )}
          {actions.includes("resolve") && (
            <button type="button" className={styles.resolveBtn} onClick={() => open("resolve")}>
              {INCIDENT_ACTIONS.resolve.label}
            </button>
          )}
        </div>
      )}

      {mode && (
        <div className={styles.actForm}>
          <label htmlFor={noteId} className={styles.actLabel}>Note (optional)</label>
          <textarea
            id={noteId}
            className={styles.actNote}
            rows={2}
            maxLength={4000}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder={mode === "take" ? "What you are doing about it" : "What fixed it"}
            disabled={busy}
          />
          <p className={styles.actHint}>
            {mode === "take"
              ? "It stays In progress while the problem lasts and is resolved automatically once the check is healthy again."
              : "If the problem is still there, the next check opens a new incident."}
            {incident.note ? " Leave the note empty to keep the current one." : ""}
          </p>
          <div className={styles.incidentActions}>
            <button
              type="button"
              className={mode === "take" ? styles.takeBtn : styles.resolveBtn}
              onClick={submit}
              disabled={busy}
            >
              {busy ? "Saving…" : `Confirm: ${INCIDENT_ACTIONS[mode].label}`}
            </button>
            <button type="button" className={styles.cancelBtn} onClick={cancel} disabled={busy}>
              Cancel
            </button>
          </div>
        </div>
      )}
    </article>
  );
};

const Incidents = ({ incidents, onAct }) => {
  const { status, setStatus, offset, setOffset, data, loading, error, refresh, pageSize } = incidents;
  // A list left over from another tab or page is not shown under this one.
  const current = data && data.status === status && data.offset === offset ? data : null;
  const rows = current ? (status === "active" ? sortBySeverity(current.items) : current.items || []) : [];
  const pager = current ? pageInfo({ total: current.total, offset, limit: pageSize, count: rows.length }) : null;

  return (
    <section className={styles.incidents} aria-labelledby="incidents-title">
      <header className={styles.incidentsHead}>
        <span className={styles.panelIcon}><ShieldAlert size={16} /></span>
        <h2 id="incidents-title" className={styles.panelTitle}>
          Incidents
          {current && <span className={styles.incidentsCount}>{current.total ?? rows.length}</span>}
        </h2>
        <div className={styles.tabs} role="tablist" aria-label="Incident status">
          {INCIDENT_TABS.map((t) => (
            <button
              key={t.key}
              type="button"
              role="tab"
              aria-selected={status === t.key}
              className={`${styles.tab} ${status === t.key ? styles.tabActive : ""}`}
              onClick={() => status !== t.key && setStatus(t.key)}
            >
              {t.label}
            </button>
          ))}
        </div>
      </header>

      {error && (
        <div className={styles.panelError}>
          <XCircle size={14} />
          <span>{error}</span>
          <button type="button" className={styles.retryBtn} onClick={refresh}>Try again</button>
        </div>
      )}

      {!current && loading && <div className={styles.incidentsPlaceholder}>Loading incidents…</div>}

      {current && rows.length === 0 && !error && (
        <p className={styles.incidentsEmpty}>{emptyIncidentsText(status)}</p>
      )}

      {rows.length > 0 && (
        <div className={styles.incidentList}>
          {rows.map((i) => (
            <IncidentCard key={i.id} incident={i} onAct={onAct} />
          ))}
        </div>
      )}

      {pager?.show && (
        <div className={styles.pager}>
          <span>{pager.from} to {pager.to} of {pager.total}</span>
          <button type="button" className={styles.pagerBtn} disabled={!pager.hasPrev || loading} onClick={() => setOffset(pager.prevOffset)}>
            Previous
          </button>
          <button type="button" className={styles.pagerBtn} disabled={!pager.hasNext || loading} onClick={() => setOffset(pager.nextOffset)}>
            Next
          </button>
        </div>
      )}
    </section>
  );
};

const Monitoring = () => {
  const { showToast } = useToast();
  const { data, loading, error, refresh } = useMonitoring();
  const incidents = useIncidents("active");
  const incidentsError = incidents.error;
  const refreshIncidents = incidents.refresh;

  // Every failed load says so in a toast as well as on the page: the first one, a refresh, a
  // tab change. Each new failure sets the error again, so each one is announced.
  useEffect(() => { if (error) showToast(error, "error"); }, [error, showToast]);
  useEffect(() => { if (incidentsError) showToast(incidentsError, "error"); }, [incidentsError, showToast]);

  const cache = data?.cache;
  const hotelbeds = data?.hotelbeds;
  const flights = data?.flights;
  const packages = data?.packages;
  const w2m = data?.w2m;

  const verdict = verdictView(data, Date.now());
  const anyLoading = loading || incidents.loading;

  const handleRefresh = async () => {
    const [m, i] = await Promise.all([refresh(), refreshIncidents()]);
    if (m?.ok && i?.ok) showToast("Monitoring refreshed", "success");
  };

  const act = async (incident, action, note) => {
    const body = incidentPatch(action, note);
    if (!body) return false;
    try {
      await updateIncident(incident.id, body);
      showToast(actionDoneMessage(action, incident), "success");
      refreshIncidents();
      return true;
    } catch (err) {
      showToast(apiErrorMessage(err, INCIDENT_ACTIONS[action].failed), "error");
      return false;
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.toolbar}>
        <div className={styles.manager}>
          <Activity size={18} />
          Monitoring
        </div>
        <div className={styles.toolbarRight}>
          {data?.checkedAt && (
            <span className={styles.checkedAt}>Checked {timeLabel(data.checkedAt)}</span>
          )}
          <button className={styles.refreshBtn} onClick={handleRefresh} disabled={anyLoading}>
            <RefreshCw size={14} className={anyLoading ? styles.spin : ""} />
            {anyLoading ? "Checking…" : "Refresh"}
          </button>
        </div>
      </div>

      {/* The request itself failed, which is not the same as a part failing. Whatever was last
          loaded stays on screen underneath, because stale numbers next to a visible error are
          more use than a blank page when you are working out what broke. */}
      {error && (
        <div className={styles.loadError}>
          <AlertTriangle size={16} />
          {error}
          <button className={styles.retryBtn} onClick={refresh}>Try again</button>
        </div>
      )}

      {!data && loading && <div className={styles.placeholder}>Checking every part…</div>}

      {data && (
        <>
          <div className={`${styles.verdict} ${styles[`verdict${verdict.tone}`]}`}>
            <span className={styles.verdictIcon}>
              {verdict.tone === "ok" ? <CheckCircle2 size={20} /> : <AlertTriangle size={20} />}
            </span>
            <div className={styles.verdictBody}>
              <div className={styles.verdictTitle}>
                {verdict.title}
                <span className={`${styles.pill} ${styles[`pill${verdict.tone}`]}`}>{verdict.pill}</span>
              </div>
              {verdict.problems.length > 0 && (
                <ul className={styles.problems}>
                  {verdict.problems.map((p) => <li key={p}>{p}</li>)}
                </ul>
              )}
              <div className={`${styles.watch} ${verdict.watch.healthy ? "" : styles.watchWarn}`}>
                <Clock size={13} />
                <span>{verdict.watch.text}</span>
              </div>
            </div>
          </div>

          <div className={styles.grid}>
            <Panel title="Search cache" Icon={Server}
              state={panelState(cache, cacheUnhappy)} error={partError(cache)}>
              <Row label="Status">{cache?.status ?? "—"}</Row>
              <Row label="Process">{cache?.pid ? `pid ${cache.pid}` : "—"}</Row>
              <Row label="Up for">{cache?.uptimeHours != null ? ageLabel(cache.uptimeHours).replace(" ago", "") : "—"}</Row>
              <Row label="Memory">{memoryLabel(cache?.memory?.rssMB)}</Row>
              <Row label="Workers ready">
                {cache?.workers ? `${cache.workersReady} of ${cache.workers}` : "—"}
              </Row>
              <Row label="Snapshot">{cache?.snapshot ?? "—"}</Row>
            </Panel>

            <Panel title="Hotelbeds updates" Icon={Database}
              state={panelState(hotelbeds, hotelbedsUnhappy)} error={partError(hotelbeds)}>
              <Row label="Last update">
                {timeLabel(hotelbeds?.lastUpdateAt)}
                <span className={styles.sub}> · {ageLabel(hotelbeds?.lastUpdateAgeHours)}</span>
              </Row>
              <Row label="Last full">{timeLabel(hotelbeds?.lastFullAt)}</Row>
              <Row label="Failures in a row">
                {hotelbeds?.failureStreak ?? 0}
                {hotelbeds?.alertAfterFailures != null && (
                  <span className={styles.sub}> (alerts at {hotelbeds.alertAfterFailures})</span>
                )}
              </Row>
              <Row label="Run in progress">{hotelbeds?.running ? "yes" : "no"}</Row>
              <Row label="Staged ingest">{hotelbeds?.stagedIngest ? "on" : "off"}</Row>
              {hotelbeds?.staleLock && <Row label="Stale lock">left behind by a run that died</Row>}

              {recentRuns(hotelbeds).length > 0 && (
                <div className={styles.runs}>
                  <div className={styles.runsTitle}>Last runs</div>
                  {recentRuns(hotelbeds).map((r, i) => (
                    <div key={`${r.startedAt}-${i}`} className={styles.run}>
                      <span className={`${styles.runDot} ${r.status === "success" ? styles.runOk : styles.runBad}`} />
                      <span className={styles.runType}>{r.type}</span>
                      <span className={styles.runTime}>{timeLabel(r.startedAt)}</span>
                      <span className={styles.runDur}>{runDuration(r)}</span>
                      {r.error && <span className={styles.runErr} title={r.error}>{r.error}</span>}
                    </div>
                  ))}
                </div>
              )}
            </Panel>

            <Panel title="Airtürk flight imports" Icon={Plane}
              state={panelState(flights, flightsUnhappy)} error={partError(flights)}>
              <Row label="Last run">{flights?.last?.status ?? "—"}</Row>
              <Row label="Started">{timeLabel(flights?.last?.startedAt)}</Row>
              <Row label="Finished">{timeLabel(flights?.last?.finishedAt)}</Row>
              <Row label="Last success">{ageLabel(flights?.lastSuccessAgeHours)}</Row>
              <Row label="Running now">
                {flights?.running ? `${flights.running} (${flights.runningForMinutes} min)` : "no"}
              </Row>
              <Row label="Version">{flights?.last?.versionStatus ?? "—"}</Row>
              {flights?.last?.error && <Row label="Error">{flights.last.error}</Row>}
            </Panel>

            <Panel title="Package precalculation" Icon={Layers}
              state={panelState(packages, packagesUnhappy)} error={partError(packages)}>
              <Row label="Enabled">{packages?.enabled ? "yes" : "no"}</Row>
              <Row label="Running now">{packages?.running ? "yes" : "no"}</Row>
              <Row label="Every">{packages?.everyMinutes != null ? `${packages.everyMinutes} min` : "—"}</Row>
              <Row label="Last round">{minutesLabel(packages?.lastAgeMinutes)}</Row>
            </Panel>

            <Panel title="W2M hotel mappings" Icon={MapIcon}
              state={panelState(w2m, w2mUnhappy)} error={partError(w2m)}>
              <Row label="Configured">{w2m?.configured ? "yes" : "no"}</Row>
              <Row label="Total">{w2m?.mappings?.total ?? 0}</Row>
              <Row label="Verified">{w2m?.mappings?.verified ?? 0}</Row>
              <Row label="Auto-matched">{w2m?.mappings?.auto ?? 0}</Row>
              <Row label="Pending">{w2m?.mappings?.pending ?? 0}</Row>
            </Panel>
          </div>
        </>
      )}

      <Incidents incidents={incidents} onAct={act} />
    </div>
  );
};

export default Monitoring;
